// Turkey Hunt sounds. Runs on the shared arcade audio context and sound switch
// (devices/arcade/sfx.ts) so every cabinet mutes together. The gun shot and
// the turkey call are recorded samples (config shotUrl / gobbleUrl); every
// other effect is synthesised, and a synth stands in until a sample decodes.

import { arcadeContext, arcadeSfx, loadSample, playSample } from "../arcade/sfx";

let shotUrl: string | undefined;
let gobbleUrl: string | undefined;

function tone(type: OscillatorType, f0: number, f1: number, dur: number, vol = 0.18, delay = 0) {
  const a = arcadeContext();
  if (!a) return;
  const t = a.currentTime + delay;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t);
  o.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t + dur);
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  o.connect(g).connect(a.destination);
  o.start(t);
  o.stop(t + dur + 0.02);
}

function noiseBurst(dur: number, vol: number, hp = 800) {
  const a = arcadeContext();
  if (!a) return;
  const len = Math.max(1, Math.floor(a.sampleRate * dur));
  const buf = a.createBuffer(1, len, a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
  const src = a.createBufferSource();
  src.buffer = buf;
  const f = a.createBiquadFilter();
  f.type = "highpass";
  f.frequency.value = hp;
  const g = a.createGain();
  g.gain.value = vol;
  src.connect(f).connect(g).connect(a.destination);
  src.start();
}

export const sfx = {
  isEnabled: () => arcadeSfx.isEnabled(),
  setEnabled(on: boolean) {
    arcadeSfx.setEnabled(on);
  },
  configure(o: { shotUrl?: string; gobbleUrl?: string }) {
    shotUrl = o.shotUrl || undefined;
    gobbleUrl = o.gobbleUrl || undefined;
  },
  /** First gesture: create the context and decode the samples. */
  unlock() {
    if (!arcadeContext()) return;
    if (shotUrl) void loadSample(shotUrl);
    if (gobbleUrl) void loadSample(gobbleUrl);
  },
  shot() {
    if (playSample(shotUrl, 0.55, 0.97 + Math.random() * 0.06)) return;
    noiseBurst(0.12, 0.35, 600);
    tone("square", 420, 90, 0.09, 0.08);
  },
  gobble() {
    if (playSample(gobbleUrl, 0.45, 0.94 + Math.random() * 0.14)) return;
    [0, 0.09, 0.18, 0.27].forEach((d) => tone("triangle", 620, 380, 0.08, 0.06, d));
  },
  miss() {
    noiseBurst(0.07, 0.12, 2200);
  },
  hit() {
    tone("square", 660, 990, 0.08, 0.12);
    tone("square", 990, 1320, 0.12, 0.1, 0.08);
    tone("triangle", 1320, 1760, 0.18, 0.1, 0.18);
  },
  wrong() {
    tone("sawtooth", 300, 140, 0.32, 0.1);
    tone("square", 150, 90, 0.3, 0.06, 0.05);
  },
  timeout() {
    tone("square", 520, 260, 0.22, 0.09);
    tone("square", 260, 130, 0.3, 0.09, 0.22);
  },
  coin() {
    tone("square", 988, 988, 0.07, 0.1);
    tone("square", 1319, 1319, 0.24, 0.1, 0.07);
  },
  start() {
    [523, 659, 784, 1047].forEach((f, i) => tone("square", f, f, 0.1, 0.09, i * 0.09));
  },
  tick() {
    tone("square", 1200, 1200, 0.03, 0.05);
  },
  combo() {
    [784, 988, 1175, 1568].forEach((f, i) => tone("triangle", f, f * 1.01, 0.08, 0.08, i * 0.05));
  },
  fanfare() {
    [523, 659, 784, 659, 784, 1047].forEach((f, i) => tone("square", f, f, 0.14, 0.08, i * 0.13));
  },
};
